import { 
  collection, 
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { withMemberGroup } from '../utils/memberGroupPaths';
import { GeneralAssignmentConsolidatedService } from './generalAssignmentConsolidatedService';
import { ProjectAssignmentConsolidatedService } from './projectAssignmentConsolidatedService';
import { RoutineAssignmentConsolidatedService } from './routineAssignmentConsolidatedService';
import { PointManagementService } from './pointManagementService';
import { ClassMember } from '../types/alarm';
import { 
  PointsTableData, 
  StudentAssignmentHistory, 
  AssignmentPointsRecord 
} from '../types/assignmentPointsMemo';

export class PointsQueryService {
  private userId: string;
  private classId: string;
  private memberGroup?: string;
  private assignmentPointsCache: Promise<{
    generalAssignments: (AssignmentPointsRecord & { studentId: string })[];
    projectAssignments: (AssignmentPointsRecord & { studentId: string })[];
    routineAssignments: (AssignmentPointsRecord & { studentId: string })[];
  }> | null = null;
  private routineIdsCache: Promise<string[]> | null = null;

  constructor(userId: string, classId: string, memberGroup?: string) {
    this.userId = userId;
    this.classId = classId;
    this.memberGroup = memberGroup;
  }

  private loadAllAssignmentPointsCached(): Promise<{
    generalAssignments: (AssignmentPointsRecord & { studentId: string })[];
    projectAssignments: (AssignmentPointsRecord & { studentId: string })[];
    routineAssignments: (AssignmentPointsRecord & { studentId: string })[];
  }> {
    if (this.assignmentPointsCache) return this.assignmentPointsCache;
    this.assignmentPointsCache = Promise.all([
      this.loadGeneralAssignmentPoints(),
      this.loadProjectAssignmentPoints(),
      this.loadRoutineAssignmentPoints()
    ]).then(([generalAssignments, projectAssignments, routineAssignments]) => ({
      generalAssignments,
      projectAssignments,
      routineAssignments
    }));
    return this.assignmentPointsCache;
  }

  private getRoutineAssignmentIdsCached(): Promise<string[]> {
    if (this.routineIdsCache) return this.routineIdsCache;
    this.routineIdsCache = this.getRoutineAssignmentIds();
    return this.routineIdsCache;
  }

  /**
   * 특정 멤버의 과제 포인트 합계(일반+프로젝트+루틴)를 계산합니다.
   * - membersPoints(하위 컬렉션) 없이, 과제 통합 문서에서 직접 집계합니다.
   * - 주로 레벨 계산/동기화에서 사용합니다.
   */
  async getStudentAssignmentPointsTotal(studentId: string): Promise<number> {
    const { generalAssignments, projectAssignments, routineAssignments } = await this.loadAllAssignmentPointsCached();

    const all = [...generalAssignments, ...projectAssignments, ...routineAssignments];
    return all
      .filter((a: any) => a.studentId === studentId)
      .reduce((sum: number, a: any) => sum + (a?.points || 0), 0);
  }

  /**
   * 안전한 날짜 변환 함수
   */
  private safeToISOString(date: any): string {
    if (!date) return new Date().toISOString();
    
    if (date instanceof Date) {
      return date.toISOString();
    }
    
    if (typeof date === 'string') {
      return new Date(date).toISOString();
    }
    
    if (date && typeof date.toDate === 'function') {
      // Firestore Timestamp
      return date.toDate().toISOString();
    }
    
    // 기본값
    return new Date().toISOString();
  }

  /**
   * 마감일을 정렬용 전체 날짜(ISO)로 반환 (년도 포함, 모달에서 마감일 순 정렬 시 사용)
   */
  private getDueDateISO(dueDate: any): string {
    if (!dueDate) return '';
    let date: Date;
    if (dueDate instanceof Date) {
      date = dueDate;
    } else if (typeof dueDate === 'string') {
      date = new Date(dueDate);
    } else if (dueDate && typeof dueDate.toDate === 'function') {
      date = dueDate.toDate();
    } else {
      return '';
    }
    const t = date.getTime();
    return isNaN(t) ? '' : date.toISOString();
  }

  /**
   * 루틴 과제 날짜를 정렬용 전체 날짜(ISO)로 반환
   */
  private getRoutineDateISO(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const t = date.getTime();
      return isNaN(t) ? '' : date.toISOString();
    } catch {
      return '';
    }
  }

  /**
   * 모든 과제 문서에서 포인트 데이터를 조회하여 포인트 테이블 데이터 생성
   */
  async getPointsTableData(members: ClassMember[]): Promise<PointsTableData> {
    try {
      const { generalAssignments, projectAssignments, routineAssignments } = await this.loadAllAssignmentPointsCached();

      // MembersPoint-X 문서에서 긍정 포인트 조회
      const pointManagementService = new PointManagementService(this.userId, this.classId, this.memberGroup);
      const membersPointDocuments = await pointManagementService.getAllMembersPointDocuments();
      
      // 멤버별 긍정 포인트 합계 계산
      const studentRewardPoints: { [studentId: string]: number } = {};
      membersPointDocuments.forEach(doc => {
        doc.students.forEach(student => {
          const positivePoints = student.points
            .filter(point => point.pointCategory === 'positive')
            .reduce((sum, point) => sum + point.pointValue, 0);
          
          if (!studentRewardPoints[student.studentId]) {
            studentRewardPoints[student.studentId] = 0;
          }
          studentRewardPoints[student.studentId] += positivePoints;
        });
      });

      // 모든 과제 데이터를 하나의 배열로 합치기
      const allAssignments = [
        ...generalAssignments,
        ...projectAssignments,
        ...routineAssignments
      ];

      // 멤버별로 과제 기록 정리
      const studentHistories: StudentAssignmentHistory[] = members.map(member => {
        const studentAssignments = allAssignments.filter(assignment => 
          assignment.studentId === member.id
        );

        const assignmentPoints = studentAssignments.reduce((sum, assignment) => sum + assignment.points, 0);
        const rewardPoints = studentRewardPoints[member.id] || 0;
        const totalPoints = assignmentPoints + rewardPoints;

        return {
          studentId: member.id,
          studentName: member.name,
          studentNumber: member.number,
          profileImage: member.profileImage, // 프로필 이미지 필드 추가
          totalPoints,
          assignmentCount: studentAssignments.length,
          assignments: studentAssignments,
          rewardPoints // 보상 포인트 추가
        };
      });

      // 전체 통계 계산
      const totalStudents = members.length;
      const totalAssignments = allAssignments.length;
      const totalPoints = allAssignments.reduce((sum, assignment) => sum + assignment.points, 0);
      const averagePoints = totalStudents > 0 ? Math.round(totalPoints / totalStudents) : 0;

      const result: PointsTableData = {
        students: studentHistories,
        totalStudents,
        totalAssignments,
        totalPoints,
        averagePoints,
        lastUpdated: new Date(),
        membersPointDocuments // 레벨 감지 로직에서 재사용
      };

      return result;
    } catch (error) {
      console.error('❌ 포인트 테이블 데이터 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 일반 과제에서 포인트 데이터 조회 (0점 포함)
   */
  private async loadGeneralAssignmentPoints(): Promise<(AssignmentPointsRecord & { studentId: string })[]> {
    try {
      const generalService = new GeneralAssignmentConsolidatedService(this.userId, this.classId, this.memberGroup);
      const consolidatedDocs = await generalService.getAllConsolidatedDocuments();
      
      const assignments: (AssignmentPointsRecord & { studentId: string })[] = [];
      
      for (const doc of consolidatedDocs) {
        for (const assignment of doc.assignments) {
          for (const studentStatus of assignment.studentStatuses) {
            // 모든 상태의 과제를 포함 (0점 포함)
            assignments.push({
              assignmentId: assignment.assignmentId,
              title: assignment.title,
              type: 'general',
              points: studentStatus.points || 0,
              memo: studentStatus.memo || '',
              lastUpdated: this.safeToISOString(studentStatus.updatedAt),
              studentId: studentStatus.studentId,
              dueDate: this.getDueDateISO(assignment.dueDate),
              status: studentStatus.status
            });
          }
        }
      }

      return assignments;
    } catch (error) {
      console.error('❌ 일반 과제 포인트 조회 실패:', error);
      return [];
    }
  }

  /**
   * 프로젝트 과제에서 포인트 데이터 조회 (0점 포함)
   */
  private async loadProjectAssignmentPoints(): Promise<(AssignmentPointsRecord & { studentId: string })[]> {
    try {
      const projectService = new ProjectAssignmentConsolidatedService(this.userId, this.classId, this.memberGroup);
      const consolidatedDocs = await projectService.getAllConsolidatedDocuments();
      
      const assignments: (AssignmentPointsRecord & { studentId: string })[] = [];
      
      for (const doc of consolidatedDocs) {
        for (const assignment of doc.assignments) {
          for (const subTask of assignment.subTasks) {
            for (const studentStatus of subTask.studentStatuses) {
              // 모든 상태의 과제를 포함 (0점 포함)
              assignments.push({
                assignmentId: `${assignment.assignmentId}-${subTask.subTaskId}`,
                title: `${assignment.title} - ${subTask.subTaskTitle}`,
                type: 'project',
                points: studentStatus.earnedPoints || 0,
                memo: studentStatus.memo || '',
                lastUpdated: this.safeToISOString(studentStatus.lastUpdated),
                studentId: studentStatus.studentId,
                dueDate: this.getDueDateISO(assignment.dueDate),
                status: studentStatus.status
              });
            }
          }
        }
      }

      return assignments;
    } catch (error) {
      console.error('❌ 프로젝트 과제 포인트 조회 실패:', error);
      return [];
    }
  }

  /**
   * 루틴 과제에서 포인트 데이터 조회 (0점 포함)
   */
  private async loadRoutineAssignmentPoints(): Promise<(AssignmentPointsRecord & { studentId: string })[]> {
    try {
      const routineService = new RoutineAssignmentConsolidatedService(this.userId, this.classId, this.memberGroup);
      
      // 모든 루틴 과제 ID 조회
      const routineIds = await this.getRoutineAssignmentIdsCached();
      const assignments: (AssignmentPointsRecord & { studentId: string })[] = [];
      
      for (const routineId of routineIds) {
        try {
        const consolidatedDocs = await routineService.getAllConsolidatedDocuments(routineId);
        for (const doc of consolidatedDocs) {
            if (!doc.dateAssignments || doc.dateAssignments.length === 0) {
              continue;
            }
            
          for (const dateAssignment of doc.dateAssignments) {
              if (dateAssignment.studentStatuses && dateAssignment.studentStatuses.length > 0) {
              for (const studentStatus of dateAssignment.studentStatuses) {
                // 모든 상태의 과제를 포함 (0점 포함)
                  const points = studentStatus.points || 0;
                assignments.push({
                  assignmentId: `${doc.routineAssignmentId}-${dateAssignment.date}`,
                  title: `${doc.title} (${dateAssignment.date})`,
                  type: 'routine',
                    points: points,
                  memo: studentStatus.memo || '',
                  lastUpdated: this.safeToISOString(studentStatus.updatedAt),
                  studentId: studentStatus.studentId,
                  dueDate: this.getRoutineDateISO(dateAssignment.date),
                  status: studentStatus.status
                });
          }
              }
            }
          }
        } catch (error) {
          console.error(`❌ 루틴 과제 ${routineId} 조회 실패:`, error);
        }
      }

      return assignments;
    } catch (error) {
      console.error('❌ 루틴 과제 포인트 조회 실패:', error);
      return [];
    }
  }

  /**
   * 루틴 과제 ID 목록 조회
   * routineAssignments 컬렉션과, 포인트 집계와 동일한 그룹 경로의 routineAssignmentStatuses[_G]에서 조회
   * (이전에는 A 경로만 나열해 B/C 그룹에서 상태는 있는데 ID가 안 잡혀 0점으로 보이는 문제가 있었음)
   */
  private async getRoutineAssignmentIds(): Promise<string[]> {
    try {
      const routineIdsSet = new Set<string>();
      const statusesRoot = withMemberGroup('routineAssignmentStatuses', this.memberGroup);
      
      // 1. routineAssignments 컬렉션에서 루틴 과제 ID 조회 (설정 문서)
      try {
        const routineAssignmentsRef = collection(db, `users/${this.userId}/classes/${this.classId}/routineAssignments`);
        const routineAssignmentsSnap = await getDocs(routineAssignmentsRef);
        routineAssignmentsSnap.docs.forEach(doc => {
          routineIdsSet.add(doc.id);
        });
      } catch (error) {
        console.warn('⚠️ routineAssignments 컬렉션 조회 실패:', error);
      }
      
      // 2. 그룹별 routineAssignmentStatuses[_G]에서 루틴 과제 ID 조회 (RoutineAssignmentConsolidatedService와 동일 경로)
      try {
        const routineStatusesRef = collection(db, `users/${this.userId}/classes/${this.classId}/${statusesRoot}`);
        const routineStatusesSnap = await getDocs(routineStatusesRef);
        routineStatusesSnap.docs.forEach(doc => {
          routineIdsSet.add(doc.id);
        });
      } catch (error) {
        console.warn(`⚠️ ${statusesRoot} 컬렉션 조회 실패:`, error);
      }
      
      const routineIds = Array.from(routineIdsSet);
      return routineIds;
    } catch (error) {
      console.error('❌ 루틴 과제 ID 조회 실패:', error);
      return [];
    }
  }

  /**
   * 특정 멤버의 포인트 기록 조회
   */
  async getStudentPointsHistory(studentId: string): Promise<AssignmentPointsRecord[]> {
    try {
      const { generalAssignments, projectAssignments, routineAssignments } = await this.loadAllAssignmentPointsCached();

      const allAssignments = [
        ...generalAssignments,
        ...projectAssignments,
        ...routineAssignments
      ];

      return allAssignments
        .filter(assignment => assignment.studentId === studentId)
        .map(assignment => {
          const { studentId, ...assignmentRecord } = assignment;
          return assignmentRecord;
        });
    } catch (error) {
      console.error('❌ 멤버 포인트 기록 조회 실패:', error);
      throw error;
    }
  }
}

export default PointsQueryService;
